import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { HeroPerfReport } from "@/lib/hero-perf";
import { HeroBudgetEditor } from "@/components/qa/HeroBudgetEditor";
import { SrcsetVerifyPanel } from "@/components/qa/SrcsetVerifyPanel";
import {
  groupSeries,
  loadSamples,
  persistSamples,
  p75,
  mean,
  samplesToCsv,
  toSample,
  type HeroPerfSample,
} from "@/lib/hero-perf-samples";

type Preset = { label: string; width: number; height: number };

const PRESETS: Preset[] = [
  { label: "iPhone SE (375)", width: 375, height: 667 },
  { label: "iPhone 14 (390)", width: 390, height: 844 },
  { label: "Pixel 7 (412)", width: 412, height: 915 },
  { label: "iPad mini (768)", width: 768, height: 1024 },
  { label: "iPad Pro (1024)", width: 1024, height: 1366 },
  { label: "Laptop (1440)", width: 1440, height: 900 },
  { label: "Desktop (1920)", width: 1920, height: 1080 },
];

const DIMENSIONS = [
  { key: "breakpoint", label: "Breakpoint" },
  { key: "dpr", label: "Device pixel ratio" },
  { key: "connection", label: "Connection type" },
  { key: "label", label: "Viewport" },
] as const;

type Dimension = (typeof DIMENSIONS)[number]["key"];

const PHASES = [
  { key: "networkMs", label: "Network (start→responseEnd)", color: "#6ea8fe" },
  { key: "loadMs", label: "Load dispatch", color: "#ffd479" },
  { key: "decodeMs", label: "Decode", color: "#8ee6a8" },
] as const;

/** Loads the hero frame in an offscreen iframe and resolves its perf report. */
function measureViewport(
  host: HTMLElement,
  preset: Preset,
  timeoutMs = 9000,
): Promise<HeroPerfReport | null> {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.style.cssText = `width:${preset.width}px;height:${preset.height}px;border:0;position:absolute;left:-10000px;top:0;`;
    iframe.src = `/qa/hero/frame?perf=1&cb=${Date.now()}-${Math.random()}`;
    let settled = false;
    let poll: number | null = null;

    const finish = (report: HeroPerfReport | null) => {
      if (settled) return;
      settled = true;
      if (poll != null) window.clearInterval(poll);
      window.clearTimeout(timer);
      try {
        iframe.contentWindow?.removeEventListener(
          "hero-perf",
          onPerf as EventListener,
        );
      } catch {
        /* cross-origin guard — same-origin here, but be safe */
      }
      host.removeChild(iframe);
      resolve(report);
    };

    const onPerf = (e: Event) =>
      finish((e as CustomEvent<HeroPerfReport>).detail ?? null);

    const timer = window.setTimeout(() => finish(null), timeoutMs);

    iframe.addEventListener("load", () => {
      const win = iframe.contentWindow as (Window & {
        __heroPerf?: HeroPerfReport;
      }) | null;
      if (!win) return finish(null);
      win.addEventListener("hero-perf", onPerf as EventListener);
      // The report can land before the iframe's load event when the image is
      // served from cache, so poll the window global as a backstop.
      poll = window.setInterval(() => {
        if (win.__heroPerf) finish(win.__heroPerf);
      }, 120);
    });

    host.appendChild(iframe);
  });
}

export default function QAHeroPerf() {
  const [samples, setSamples] = useState<HeroPerfSample[]>(() => loadSamples());
  const [dimension, setDimension] = useState<Dimension>("breakpoint");
  const [selected, setSelected] = useState<string[]>(() =>
    PRESETS.map((p) => p.label),
  );
  const [runs, setRuns] = useState(1);
  const [profile, setProfile] = useState("");
  const [running, setRunning] = useState<null | { done: number; total: number }>(
    null,
  );
  const [filters, setFilters] = useState({
    breakpoint: "all",
    dpr: "all",
    connection: "all",
  });
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    persistSamples(samples);
  }, [samples]);

  // Also capture any hero-perf report fired by this tab (e.g. if the dashboard
  // is opened after browsing the homepage in the same session).
  useEffect(() => {
    const onPerf = (e: Event) => {
      const detail = (e as CustomEvent<HeroPerfReport>).detail;
      if (!detail) return;
      setSamples((prev) => [
        ...prev,
        toSample(detail, {
          label: `Live (${detail.viewportWidth}px)`,
          connectionOverride: profile || undefined,
        }),
      ]);
    };
    window.addEventListener("hero-perf", onPerf as EventListener);
    return () => window.removeEventListener("hero-perf", onPerf as EventListener);
  }, [profile]);

  const options = useMemo(
    () => ({
      breakpoint: [...new Set(samples.map((s) => s.breakpoint))].sort(),
      dpr: [...new Set(samples.map((s) => String(s.dpr)))].sort(),
      connection: [...new Set(samples.map((s) => s.connection))].sort(),
    }),
    [samples],
  );

  const filtered = useMemo(
    () =>
      samples.filter(
        (s) =>
          (filters.breakpoint === "all" || s.breakpoint === filters.breakpoint) &&
          (filters.dpr === "all" || String(s.dpr) === filters.dpr) &&
          (filters.connection === "all" || s.connection === filters.connection),
      ),
    [samples, filters],
  );

  const series = useMemo(
    () => groupSeries(filtered, dimension),
    [filtered, dimension],
  );

  const timeline = useMemo(
    () =>
      [...filtered]
        .sort((a, b) => a.at.localeCompare(b.at))
        .slice(-40)
        .map((s, i) => ({
          idx: i + 1,
          when: new Date(s.at).toLocaleTimeString(),
          label: s.label,
          totalMs: s.totalMs ?? 0,
          lcpMs: s.lcpMs ?? 0,
        })),
    [filtered],
  );

  const runMeasurements = async () => {
    const host = hostRef.current;
    if (!host) return;
    const targets = PRESETS.filter((p) => selected.includes(p.label));
    const total = targets.length * runs;
    if (!total) return;
    setRunning({ done: 0, total });
    const collected: HeroPerfSample[] = [];
    let done = 0;
    for (let r = 0; r < runs; r++) {
      for (const preset of targets) {
        const report = await measureViewport(host, preset);
        if (report) {
          collected.push(
            toSample(report, {
              label: preset.label,
              connectionOverride: profile || undefined,
            }),
          );
        }
        done += 1;
        setRunning({ done, total });
      }
    }
    setSamples((prev) => [...prev, ...collected]);
    setRunning(null);
  };

  const exportCsv = () => {
    const blob = new Blob([samplesToCsv(filtered)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hero-perf-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = (label: string) =>
    setSelected((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );

  const lcpP75 = p75(filtered, "lcpMs");
  const totalP75 = p75(filtered, "totalMs");
  const avgTransfer = mean(filtered, "transferKb");

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/60 bg-navy-elevated/40">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-lg tracking-[0.2em] uppercase text-gold">
              Hero LCP timeline
            </h1>
            <Link
              to="/qa/hero"
              className="text-xs text-silver/70 underline hover:text-gold"
            >
              ← Crop tuning
            </Link>
          </div>
          <p className="mt-1 text-xs text-silver/70">
            Measures start → responseEnd → load → decoded → LCP for the hero
            image at each viewport, bucketed by breakpoint, device pixel ratio,
            and connection type. Runs use the same component the homepage
            renders, in an offscreen frame.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
            <button
              type="button"
              onClick={runMeasurements}
              disabled={!!running || selected.length === 0}
              className="rounded border border-emerald/60 px-3 py-1.5 text-emerald hover:bg-emerald/10 disabled:opacity-60"
            >
              {running
                ? `Measuring ${running.done}/${running.total}…`
                : `Run measurement (${selected.length * runs})`}
            </button>
            <label className="flex items-center gap-2">
              Runs per viewport
              <input
                type="number"
                min={1}
                max={10}
                value={runs}
                onChange={(e) =>
                  setRuns(Math.max(1, Math.min(10, Number(e.target.value) || 1)))
                }
                className="w-16 rounded border border-border bg-transparent px-2 py-1"
              />
            </label>
            <label className="flex items-center gap-2">
              Network label
              <input
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
                placeholder="auto (navigator.connection)"
                className="w-52 rounded border border-border bg-transparent px-2 py-1"
                title="Tag samples with the throttling profile you set in DevTools (e.g. Fast 3G)"
              />
            </label>
            <button
              type="button"
              onClick={exportCsv}
              disabled={filtered.length === 0}
              className="rounded border border-border px-3 py-1.5 text-silver/80 hover:bg-white/5 disabled:opacity-50"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => setSamples([])}
              disabled={samples.length === 0}
              className="rounded border border-border px-3 py-1.5 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
            >
              Clear samples
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-3 text-xs text-silver/80">
            {PRESETS.map((p) => (
              <label key={p.label} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selected.includes(p.label)}
                  onChange={() => toggle(p.label)}
                />
                {p.label}
              </label>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 space-y-6">
        <section className="grid gap-3 sm:grid-cols-4">
          {[
            { label: "Samples", value: `${filtered.length}` },
            { label: "LCP p75", value: lcpP75 != null ? `${lcpP75} ms` : "—" },
            {
              label: "Paintable p75",
              value: totalP75 != null ? `${totalP75} ms` : "—",
            },
            {
              label: "Avg transfer",
              value: avgTransfer != null ? `${avgTransfer} KB` : "cached / —",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded border border-border/60 bg-navy-elevated/40 p-3"
            >
              <p className="text-[10px] uppercase tracking-[0.25em] text-silver/60">
                {card.label}
              </p>
              <p className="mt-1 font-display text-xl text-gold tabular-nums">
                {card.value}
              </p>
            </div>
          ))}
        </section>

        <HeroBudgetEditor samples={samples} />

        <SrcsetVerifyPanel />

        <section className="rounded border border-border/60 bg-navy-elevated/40 p-4">
          <div className="mb-3 flex flex-wrap items-center gap-4 text-xs text-silver/80">
            <label className="flex items-center gap-2">
              Group by
              <select
                value={dimension}
                onChange={(e) => setDimension(e.target.value as Dimension)}
                className="rounded border border-border bg-navy-elevated px-2 py-1"
              >
                {DIMENSIONS.map((d) => (
                  <option key={d.key} value={d.key}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
            {(["breakpoint", "dpr", "connection"] as const).map((f) => (
              <label key={f} className="flex items-center gap-2 capitalize">
                {f}
                <select
                  value={filters[f]}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, [f]: e.target.value }))
                  }
                  className="rounded border border-border bg-navy-elevated px-2 py-1"
                >
                  <option value="all">all</option>
                  {options[f].map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {series.length === 0 ? (
            <p className="py-16 text-center text-sm text-silver/60">
              No samples yet — run a measurement to populate the timeline.
            </p>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff18" />
                  <XAxis dataKey="key" stroke="#c9d1d9" fontSize={11} />
                  <YAxis
                    stroke="#c9d1d9"
                    fontSize={11}
                    label={{
                      value: "ms",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#c9d1d9",
                      fontSize: 11,
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0b1220",
                      border: "1px solid #ffffff22",
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {PHASES.map((p) => (
                    <Bar
                      key={p.key}
                      dataKey={p.key}
                      stackId="phases"
                      name={p.label}
                      fill={p.color}
                    />
                  ))}
                  <Bar dataKey="lcpMs" name="LCP" fill="#e8b45a">
                    {series.map((s) => (
                      <Cell
                        key={s.key}
                        fill={s.lcpMs > 2500 ? "#f87171" : "#e8b45a"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="rounded border border-border/60 bg-navy-elevated/40 p-4">
          <h2 className="mb-3 font-display text-xs uppercase tracking-[0.25em] text-gold">
            Recent runs (paintable vs LCP)
          </h2>
          {timeline.length === 0 ? (
            <p className="py-10 text-center text-sm text-silver/60">
              Nothing recorded yet.
            </p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff18" />
                  <XAxis dataKey="label" stroke="#c9d1d9" fontSize={10} hide />
                  <YAxis stroke="#c9d1d9" fontSize={11} />
                  <Tooltip
                    contentStyle={{
                      background: "#0b1220",
                      border: "1px solid #ffffff22",
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="totalMs" name="Paintable (ms)" fill="#8ee6a8" />
                  <Bar dataKey="lcpMs" name="LCP (ms)" fill="#e8b45a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="rounded border border-border/60 bg-navy-elevated/40 p-4 overflow-x-auto">
          <h2 className="mb-3 font-display text-xs uppercase tracking-[0.25em] text-gold">
            Sample detail
          </h2>
          <table className="w-full text-left text-xs text-silver/80">
            <thead className="text-silver/60">
              <tr>
                {[
                  "When",
                  "Viewport",
                  "BP",
                  "DPR",
                  "Conn",
                  "Network",
                  "Load",
                  "Decode",
                  "Paintable",
                  "LCP",
                  "KB",
                ].map((h) => (
                  <th key={h} className="px-2 py-1 font-normal">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...filtered]
                .sort((a, b) => b.at.localeCompare(a.at))
                .slice(0, 60)
                .map((s) => (
                  <tr key={s.id} className="border-t border-border/40 tabular-nums">
                    <td className="px-2 py-1">
                      {new Date(s.at).toLocaleTimeString()}
                    </td>
                    <td className="px-2 py-1">{s.label}</td>
                    <td className="px-2 py-1">{s.breakpoint}</td>
                    <td className="px-2 py-1">{s.dpr}</td>
                    <td className="px-2 py-1">{s.connection}</td>
                    <td className="px-2 py-1">{s.networkMs ?? "—"}</td>
                    <td className="px-2 py-1">{s.loadMs ?? "—"}</td>
                    <td className="px-2 py-1">{s.decodeMs ?? "—"}</td>
                    <td className="px-2 py-1">{s.totalMs ?? "—"}</td>
                    <td className="px-2 py-1">{s.lcpMs ?? "—"}</td>
                    <td className="px-2 py-1">{s.transferKb ?? "—"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </section>
      </main>

      {/* Offscreen host for measurement iframes. */}
      <div ref={hostRef} aria-hidden className="pointer-events-none absolute" />
    </div>
  );
}
